import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Select,
  Row,
  Col,
  message,
  Tag,
  Spin,
  Icon,
  Input,
  Tabs,
  Modal,
  Alert,
  Tooltip,
  Empty,
  Radio,
  Statistic,
} from 'antd';
import moment from 'moment';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Papa from 'papaparse';
import { apiGet } from './utils/apiService';
import { getToken } from './utils/authService';
import { getServer } from './utils/serverConfig';

// Match the web frontend's exact GET shape for /asset-stock-taking,
// /pre-verification, /asset-link: only Authorization + the axios-style Accept
// triplet. Adding Content-Type, X-Tenant-Id, or X-Api-Version causes 406.
const rawJsonGet = async (path) => {
  const token = getToken();
  const headers = {
    Accept: 'application/json, text/plain, */*',
    Authorization: token || '',
  };
  const res = await fetch(`${getServer()}${path}`, { method: 'GET', headers });
  const text = await res.text();
  if (!res.ok) {
    console.warn('[AuditCoverage] non-OK response', path, res.status, text.slice(0, 200));
    return { success: false, status: res.status, error: text };
  }
  try {
    const data = JSON.parse(text);
    return { success: true, data };
  } catch (e) {
    console.warn('[AuditCoverage] non-JSON response', path, text.slice(0, 200));
    return { success: false, error: 'non-json response' };
  }
};

const { Option } = Select;
const { TabPane } = Tabs;

const extract = (res) => {
  if (!res || res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  if (d && Array.isArray(d.assets)) return d.assets;
  if (d && Array.isArray(d.results)) return d.results;
  if (d && Array.isArray(d.rows)) return d.rows;
  return [];
};

const idOf = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') return raw._id || raw.id || null;
  return null;
};

const nameOf = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') return raw.name || raw.code || raw.altName || '';
  return '';
};

// Build a double-encoded filters param: a JSON array containing JSON-encoded
// filter strings. This is what the AssetPro API expects (matches the web
// frontend and the Flutter app's /asset-link call).
//   filters=["{\"value\":[[\"...\"]],\"type\":\"ObjectID\",\"field\":\"...\"}"]
const buildFilters = (pairs) => {
  const stringified = pairs
    .filter(([, value]) => value)
    .map(([field, value]) =>
      JSON.stringify({ value: [[value]], type: 'ObjectID', field })
    );
  return stringified.length ? JSON.stringify(stringified) : null;
};

// Best-effort extraction of photo URLs from a record / asset-detail item.
const collectPhotos = (item) => {
  const urls = [];
  const push = (u) => {
    if (!u) return;
    if (typeof u === 'string') urls.push(u);
    else if (u.url) urls.push(u.url);
    else if (u.path) urls.push(u.path);
  };
  ['photos', 'images', 'fileData', 'attachments'].forEach((key) => {
    const arr = item && item[key];
    if (Array.isArray(arr)) arr.forEach(push);
  });
  [
    'qrTagCloseupPath',
    'qrTagCloseupUrl',
    'qrTagCloseup',
    'installationLocationPath',
    'installationLocationUrl',
    'installationLocation',
    'nameplatePath',
    'nameplateUrl',
    'nameplate',
    'photoUrl',
    'imageUrl',
  ].forEach((key) => push(item && item[key]));
  return urls;
};

// AST assetDetails entry shape (from the Flutter client sync code):
//   { assetCode, qrId, manual (bool), isQrMismatched (bool),
//     link_record ('AT-N'), condition (id or populated object),
//     remarks, fileData, time, user, tag_type (id or object),
//     existingAsset (populated ref to asset master),
//     assetType / equipmentSubType / assetModel (PV only, populated),
//     tempAssetCode (PV only), latitude, longitude }
//
// "Entry Type" in the UI is derived from `manual`: manual=false → QR Code,
// manual=true → Manual.

const matchedAssetIdOf = (detail) =>
  idOf(detail.existingAsset) ||
  idOf(detail.asset) ||
  idOf(detail.matchedAsset) ||
  detail.matchedAssetId ||
  detail.assetId ||
  null;

const matchedAssetCodeOf = (detail, fallback = '') => {
  const ea = detail.existingAsset;
  return (
    detail.assetCode ||
    detail.existingAssetCode ||
    (ea && typeof ea === 'object' && ea.assetCode) ||
    detail.matchedAssetCode ||
    (detail.matchedAsset && detail.matchedAsset.assetCode) ||
    (detail.asset && detail.asset.assetCode) ||
    fallback
  );
};

const tempCodeOf = (detail) =>
  detail.tempAssetCode || detail.tempCode || detail.temporaryAssetCode || '';

const linkCodeOf = (detail) =>
  detail.link_record ||
  detail.linkRecord ||
  detail.linkCode ||
  detail.assetLinkCode ||
  (detail.linkData && detail.linkData.code) ||
  (detail.assetLink && detail.assetLink.code) ||
  '';

const qrValueOf = (detail) =>
  detail.qrId ||
  detail.qr ||
  detail.qrCode ||
  detail.qrValue ||
  detail.scannedQr ||
  '';

// Entry type is encoded as the `manual` boolean on AST records. If the
// server populates an explicit `type` field, use it as a tiebreaker.
const isManualEntry = (detail) => {
  if (typeof detail.manual === 'boolean') return detail.manual === true;
  if (detail.manual === 1 || detail.manual === '1' || detail.manual === 'true') return true;
  return /manual/i.test(detail.type || detail.entryType || '');
};
const isQrEntry = (detail) => {
  if (typeof detail.manual === 'boolean') return detail.manual === false;
  if (detail.manual === 0 || detail.manual === '0' || detail.manual === 'false') return true;
  return /qr/i.test(detail.type || detail.entryType || '');
};
const entryTypeOf = (detail) => {
  if (isQrEntry(detail)) return 'QR';
  if (isManualEntry(detail)) return 'Manual';
  return '';
};
const entryTypeLabel = (detail) => {
  if (isQrEntry(detail)) return 'QR Code';
  if (isManualEntry(detail)) return 'Manual';
  return '';
};

// Classify each AST detail per the audit semantics:
//   - QR Code entry + !mismatch          → "QR Verified" (system QR matches physical)
//   - QR Code entry + mismatch           → "QR Mismatch"
//   - Manual entry + new AT-N link       → "New QR Linked" (asset had no QR; now tagged)
//   - Manual entry + no link             → "Verified, No QR" (visual only, no QR present)
//   - anything else                      → "Verified"
// If the entry also creates a new AT-N alongside a QR scan we annotate it.
const classifyAst = (detail) => {
  const mismatched = !!detail.isQrMismatched;
  const link = linkCodeOf(detail);
  const qrEntry = isQrEntry(detail);
  const manualEntry = isManualEntry(detail);
  if (qrEntry) {
    if (mismatched) return { code: 'qr_mismatch', label: 'QR Mismatch', color: 'red' };
    return { code: 'qr_verified', label: 'QR Verified', color: 'green' };
  }
  if (manualEntry) {
    if (link) return { code: 'new_qr_linked', label: 'New QR Linked', color: 'blue' };
    return { code: 'verified_no_qr', label: 'Verified, No QR', color: 'orange' };
  }
  // Fallback when entry type isn't explicit on the record.
  if (mismatched) return { code: 'qr_mismatch', label: 'QR Mismatch', color: 'red' };
  if (link) return { code: 'new_qr_linked', label: 'New QR Linked', color: 'blue' };
  return { code: 'verified', label: 'Verified', color: 'green' };
};

// An asset is considered "has QR after audit" if it ended the audit with a
// working QR tag — either the existing QR matched (qr_verified) or a new QR
// was attached (new_qr_linked). Mismatches and "verified, no QR" are gaps.
const HAS_QR_CODES = new Set(['qr_verified', 'new_qr_linked']);
const QR_GAP_CODES = new Set(['qr_mismatch', 'verified_no_qr']);

const SUMMARY_TILE_COL = { xs: 24, sm: 12, md: 8, lg: 8, xl: 8 };

const AuditCoverageReport = () => {
  const [loading, setLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [vouchers, setVouchers] = useState([]);

  // All filters are multi-select: an empty array means "All".
  const [locationIds, setLocationIds] = useState([]);
  // "Client" in the API is called "Sub Location" in this report's UI.
  const [clientIds, setClientIds] = useState([]);
  const [voucherId, setVoucherId] = useState(null);

  const [assetTypeIds, setAssetTypeIds] = useState([]);
  const [assetSubTypeIds, setAssetSubTypeIds] = useState([]);
  const [assetModelIds, setAssetModelIds] = useState([]);

  const [assetTypes, setAssetTypes] = useState([]);
  const [assetSubTypes, setAssetSubTypes] = useState([]);
  const [assetModels, setAssetModels] = useState([]);

  const [assets, setAssets] = useState([]);
  const [astRecords, setAstRecords] = useState([]);
  const [pvRecords, setPvRecords] = useState([]);
  // Asset-link records keyed by their AT-N code.
  const [linksByCode, setLinksByCode] = useState({});
  // Master lookups (id → display name)
  const [lookups, setLookups] = useState({
    conditionsById: {},
    tagTypesById: {},
    usersById: {},
  });

  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [astSearch, setAstSearch] = useState('');
  const [photoModal, setPhotoModal] = useState({ visible: false, urls: [], title: '' });
  const [verificationsModal, setVerificationsModal] = useState({
    visible: false,
    assetCode: '',
    entries: [],
  });

  // Initial load: locations, clients, audit vouchers
  useEffect(() => {
    const load = async () => {
      setBootLoading(true);
      try {
        const opts = { headers: { Accept: '*/*' } };
        const [
          locRes,
          clientRes,
          voucherRes,
          condRes,
          tagRes,
          userRes,
          typeRes,
          subTypeRes,
          modelRes,
        ] = await Promise.all([
          apiGet('/location', opts),
          apiGet('/client', opts),
          rawJsonGet('/audit-voucher'),
          rawJsonGet('/condition-of-asset'),
          rawJsonGet('/asset-tag-type'),
          rawJsonGet('/user?limit=2000'),
          rawJsonGet('/asset-type?limit=2000'),
          rawJsonGet('/equipment-sub-type?limit=2000'),
          rawJsonGet('/asset-model?limit=5000'),
        ]);
        const locs = extract(locRes);
        const cls = extract(clientRes);
        const vs = extract(voucherRes);
        setLocations(locs);
        setClients(cls);
        setVouchers(vs);
        setAssetTypes(extract(typeRes));
        setAssetSubTypes(extract(subTypeRes));
        setAssetModels(extract(modelRes));

        const conditionsById = {};
        extract(condRes).forEach((c) => {
          conditionsById[c._id] = c.name || c.code || '';
        });
        const tagTypesById = {};
        extract(tagRes).forEach((t) => {
          tagTypesById[t._id] = t.name || t.code || '';
        });
        const usersById = {};
        extract(userRes).forEach((u) => {
          usersById[u._id] = u.name || u.email || '';
        });
        setLookups({ conditionsById, tagTypesById, usersById });

        // Best-effort defaults: Bird group + Delhi Airport.
        const birdClient = cls.find((c) =>
          /bird/i.test(c.name || c.code || '')
        );
        if (birdClient) setClientIds([birdClient._id]);

        const delhi = locs.find((l) => /delhi.*airport|igi/i.test(l.name || ''));
        if (delhi) setLocationIds([delhi._id]);
      } catch (e) {
        console.error('Audit Coverage boot load failed', e);
        message.error('Failed to load locations / clients / vouchers');
      } finally {
        setBootLoading(false);
      }
    };
    load();
  }, []);

  // Audit vouchers are organisation-scoped — they don't carry location/client.
  // Show all and let the user pick by name (e.g. "Delhi Airport Audit").
  const filteredVouchers = useMemo(() => vouchers, [vouchers]);

  // Cascading filter options. Empty parent selection = no constraint.
  const subTypeOptions = useMemo(() => {
    if (!assetTypeIds.length) return assetSubTypes;
    const set = new Set(assetTypeIds);
    return assetSubTypes.filter((s) => set.has(idOf(s.assetType)));
  }, [assetSubTypes, assetTypeIds]);

  const modelOptions = useMemo(() => {
    if (assetSubTypeIds.length) {
      const set = new Set(assetSubTypeIds);
      return assetModels.filter((m) => set.has(idOf(m.assetSubType)));
    }
    if (assetTypeIds.length) {
      const set = new Set(assetTypeIds);
      return assetModels.filter((m) =>
        set.has(idOf(m.assetSubType && m.assetSubType.assetType))
      );
    }
    return assetModels;
  }, [assetModels, assetTypeIds, assetSubTypeIds]);

  // Reset cascading children when a parent changes — drop any selected
  // child id that no longer fits the parent set.
  useEffect(() => {
    if (!assetSubTypeIds.length) return;
    const valid = new Set(subTypeOptions.map((s) => s._id));
    const kept = assetSubTypeIds.filter((id) => valid.has(id));
    if (kept.length !== assetSubTypeIds.length) setAssetSubTypeIds(kept);
  }, [subTypeOptions, assetSubTypeIds]);
  useEffect(() => {
    if (!assetModelIds.length) return;
    const valid = new Set(modelOptions.map((m) => m._id));
    const kept = assetModelIds.filter((id) => valid.has(id));
    if (kept.length !== assetModelIds.length) setAssetModelIds(kept);
  }, [modelOptions, assetModelIds]);

  // Predicate: does this master asset pass the active asset filters?
  // Asset master: equipmentSubType (id string), assetModel (populated object).
  const assetPassesFilters = useCallback(
    (a) => {
      if (!a) return false;
      const modelObj = a.assetModel && typeof a.assetModel === 'object' ? a.assetModel : null;
      const modelId = idOf(a.assetModel);
      const subTypeIdOnAsset = idOf(a.equipmentSubType);
      const subTypeRec = subTypeIdOnAsset
        ? assetSubTypes.find((s) => s._id === subTypeIdOnAsset)
        : null;
      const typeIdOnAsset =
        idOf(subTypeRec && subTypeRec.assetType) ||
        idOf(modelObj && modelObj.assetSubType && modelObj.assetSubType.assetType);

      if (assetTypeIds.length && !assetTypeIds.includes(typeIdOnAsset)) return false;
      if (assetSubTypeIds.length && !assetSubTypeIds.includes(subTypeIdOnAsset)) return false;
      if (assetModelIds.length && !assetModelIds.includes(modelId)) return false;
      return true;
    },
    [assetSubTypes, assetTypeIds, assetSubTypeIds, assetModelIds]
  );

  // Auto-pick latest voucher when location changes.
  useEffect(() => {
    if (!filteredVouchers.length) {
      setVoucherId(null);
      return;
    }
    // Prefer "active"/"OPEN" voucher, else latest by createdAt.
    const sorted = [...filteredVouchers].sort((a, b) => {
      const aActive = /open|active/i.test(a.status || '') ? 1 : 0;
      const bActive = /open|active/i.test(b.status || '') ? 1 : 0;
      if (aActive !== bActive) return bActive - aActive;
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
    if (!voucherId || !filteredVouchers.some((v) => v._id === voucherId)) {
      setVoucherId(sorted[0]._id);
    }
  }, [filteredVouchers, voucherId]);

  const fetchAuditData = useCallback(async () => {
    if (!locationIds.length) {
      message.warning('Select at least one location first');
      return;
    }
    setLoading(true);
    try {
      // /asset/asset-all needs Accept: */* (no Content-Type), but
      // /asset-stock-taking and /pre-verification reject */* with 406 and
      // require Accept: application/json (the apiGet default).
      const assetListOpts = { headers: { Accept: '*/*' } };
      // Match the Flutter client: only filter by auditVoucher on the server,
      // then filter by location client-side.
      const voucherFilter = voucherId
        ? buildFilters([['auditVoucher', voucherId]])
        : null;

      const astUrl = `/asset-stock-taking${
        voucherFilter ? `?filters=${encodeURIComponent(voucherFilter)}` : ''
      }`;
      const pvUrl = `/pre-verification${
        voucherFilter ? `?filters=${encodeURIComponent(voucherFilter)}` : ''
      }`;

      const calls = [
        apiGet(`/asset/asset-all?limit=10000&offset=0`, assetListOpts),
        rawJsonGet(astUrl),
        rawJsonGet(pvUrl),
      ];
      console.log('[AuditCoverage] fetch URLs', { astUrl, pvUrl, locationIds, voucherId });
      const [assetRes, astRes, pvRes] = await Promise.all(calls);
      console.log('[AuditCoverage] raw responses', { assetRes, astRes, pvRes });

      const locSet = new Set(locationIds);
      const allAssets = extract(assetRes);
      const locAssets = allAssets.filter((a) => locSet.has(idOf(a.location)));

      // Client-side location filter (matching Flutter behavior). Records
      // without a location field are kept.
      const ast = extract(astRes).filter((r) => {
        const rLoc = idOf(r.location);
        if (rLoc && !locSet.has(rLoc)) return false;
        return true;
      });
      const pv = extract(pvRes).filter((r) => {
        const rLoc = idOf(r.location);
        if (rLoc && !locSet.has(rLoc)) return false;
        return true;
      });
      console.log('[AuditCoverage] filtered counts', {
        assetsAtLocation: locAssets.length,
        astTotal: extract(astRes).length,
        astAtLocation: ast.length,
        pvTotal: extract(pvRes).length,
        pvAtLocation: pv.length,
      });

      setAssets(locAssets);
      setAstRecords(ast);
      setPvRecords(pv);

      // Bulk-fetch asset-link records by link_record code referenced in AST
      // details. The /asset-link endpoint supports a multi-value String
      // filter on `code`, so we do this in a single call.
      const linkCodes = new Set();
      ast.forEach((rec) => {
        const details = Array.isArray(rec.assetDetails) ? rec.assetDetails : [];
        details.forEach((d) => {
          const code = linkCodeOf(d);
          if (code) linkCodes.add(code);
        });
      });
      const codeMap = {};
      const codes = Array.from(linkCodes);
      if (codes.length) {
        // Chunk to keep URL lengths reasonable.
        const CHUNK = 200;
        for (let i = 0; i < codes.length; i += CHUNK) {
          const slice = codes.slice(i, i + CHUNK);
          const filter = JSON.stringify([
            JSON.stringify({ value: [slice], type: 'String', field: 'code' }),
          ]);
          // eslint-disable-next-line no-await-in-loop
          const res = await rawJsonGet(
            `/asset-link?filters=${encodeURIComponent(filter)}`
          );
          extract(res).forEach((link) => {
            if (link.code) codeMap[link.code] = link;
          });
        }
      }
      setLinksByCode(codeMap);
      message.success(
        `Loaded ${locAssets.length} assets, ${ast.length} AST, ${pv.length} PV records`
      );
    } catch (e) {
      console.error('Audit Coverage fetch failed', e);
      message.error('Failed to load audit data');
    } finally {
      setLoading(false);
    }
  }, [locationIds, voucherId]);

  // Auto-fetch when at least one location is selected and a voucher is set.
  useEffect(() => {
    if (locationIds.length && !bootLoading) fetchAuditData();
  }, [locationIds, voucherId, bootLoading, fetchAuditData]);

  // ===== Derivations =====

  // Helper: from an asset-link record, find the scannerData entry that
  // corresponds to THIS link (refId === link._id) and return its QR id +
  // populated tagType name.
  const qrFromLink = useCallback((link) => {
    if (!link) return { qr: '', tagType: '' };
    const asset = link.asset && typeof link.asset === 'object' ? link.asset : null;
    const scanners = (asset && Array.isArray(asset.scannerData) ? asset.scannerData : []);
    const match = scanners.find((s) => s && s.refId && s.refId === link._id);
    if (match) {
      return {
        qr: match.id || '',
        tagType: match.tagType || lookups.tagTypesById[match.tag_type] || lookups.tagTypesById[match.tagType_id] || '',
      };
    }
    return { qr: '', tagType: '' };
  }, [lookups.tagTypesById]);

  // Map: assetCode -> list of AST entries for that asset within the current
  // audit voucher. The same asset can be verified multiple times (re-scans,
  // mismatch corrections, additional QR links) so we keep every entry and
  // sort newest first. The first entry is what drives the row-level status.
  const astByAssetCode = useMemo(() => {
    const map = {};
    astRecords.forEach((rec) => {
      const details = Array.isArray(rec.assetDetails) ? rec.assetDetails : [];
      details.forEach((d) => {
        const code = matchedAssetCodeOf(d);
        if (!code) return;
        const linkCode = linkCodeOf(d);
        const link = linkCode ? linksByCode[linkCode] : null;
        const { qr, tagType } = qrFromLink(link);
        const entry = {
          voucher: rec,
          detail: d,
          photos: collectPhotos(d).concat(collectPhotos(rec)),
          qrCode: qr || qrValueOf(d),
          tagType,
          linkCode,
          link,
          entryType: entryTypeLabel(d),
          classification: classifyAst(d),
          remarks: d.remarks || d.remark || d.notes || '',
          conditionName: lookups.conditionsById[d.condition] || '',
          userName: lookups.usersById[idOf(d.user) || d.user] || '',
          time: d.time || rec.createdAt,
          latitude: d.latitude,
          longitude: d.longitude,
          mismatched: !!d.isQrMismatched,
        };
        if (!map[code]) map[code] = [];
        map[code].push(entry);
      });
    });
    // Sort newest first per asset.
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0))
    );
    return map;
  }, [astRecords, linksByCode, lookups, qrFromLink]);

  // Build a sub-type-by-id lookup so we can show the sub-type *name* on
  // assets (where `equipmentSubType` comes back as an ID string).
  const subTypeNameById = useMemo(() => {
    const m = {};
    assetSubTypes.forEach((s) => {
      m[s._id] = s.name || s.code || '';
    });
    return m;
  }, [assetSubTypes]);

  // Build flat list of system assets with derived status.
  const systemAssetRows = useMemo(() => {
    return assets
      .filter((a) => (!clientIds.length || clientIds.includes(idOf(a.client))))
      .filter(assetPassesFilters)
      .map((a) => {
      const astEntries = astByAssetCode[a.assetCode] || [];
      const ast = astEntries[0] || null; // newest verification drives status
      const scanners = Array.isArray(a.scannerData) ? a.scannerData : [];
      const linkedQrs = scanners
        .filter((s) => s && /qr/i.test(s.type || '') && s.id)
        .map((s) => String(s.id));
      const hasScannerData = linkedQrs.length > 0;
      const verified = astEntries.length > 0;
      const classification = ast ? ast.classification : null;
      const hasQr =
        (classification && HAS_QR_CODES.has(classification.code)) ||
        (!ast && hasScannerData);
      // Aggregate photos across all verifications.
      const allPhotos = astEntries.flatMap((e) => e.photos || []);
      // Join remarks across all verifications (deduped, newest first).
      const allRemarks = Array.from(
        new Set(astEntries.map((e) => (e.remarks || '').trim()).filter(Boolean))
      );
      return {
        key: a._id,
        assetId: a._id,
        assetCode: a.assetCode,
        altName: a.altName || '',
        name: a.altName || nameOf(a.assetModel) || '',
        modelName: nameOf(a.assetModel),
        subType:
          (a.equipmentSubType && typeof a.equipmentSubType === 'object'
            ? (a.equipmentSubType.name || a.equipmentSubType.code || '')
            : subTypeNameById[a.equipmentSubType] || ''),
        ownerCode:
          a.ownerCode ||
          (a.owner && (a.owner.code || a.owner.employeeCode)) ||
          '',
        verified,
        hasQr,
        classification,
        statusLabel: classification ? classification.label : 'Pending',
        statusColor: classification ? classification.color : 'orange',
        statusCode: classification ? classification.code : 'pending',
        entryType: ast ? ast.entryType : '',
        remarks: allRemarks.join(' • '),
        astVoucherNumber:
          (ast && (ast.voucher.astNo || ast.voucher.voucherNumber || ast.voucher.voucherNo)) || '',
        linkCode: (ast && ast.linkCode) || '',
        qrCode: (ast && ast.qrCode) || '',
        linkedQrs,
        linkedQrsCount: linkedQrs.length,
        linkedQrsText: linkedQrs.join(', '),
        photoUrls: allPhotos,
        astEntries,
        astCount: astEntries.length,
      };
    });
  }, [assets, astByAssetCode, clientIds, assetPassesFilters, subTypeNameById]);

  // Filtered rows for the System Assets tab.
  const filteredSystemRows = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    return systemAssetRows.filter((r) => {
      switch (statusFilter) {
        case 'verified':
          if (!r.verified) return false;
          break;
        case 'pending':
          if (r.verified) return false;
          break;
        case 'qr_verified':
          if (r.statusCode !== 'qr_verified') return false;
          break;
        case 'qr_mismatch':
          if (r.statusCode !== 'qr_mismatch') return false;
          break;
        case 'new_qr_linked':
          if (r.statusCode !== 'new_qr_linked') return false;
          break;
        case 'verified_no_qr':
          if (r.statusCode !== 'verified_no_qr') return false;
          break;
        case 'qr_gap':
          if (!QR_GAP_CODES.has(r.statusCode) && r.verified) return false;
          if (!r.verified) return false;
          break;
        default:
          break;
      }
      if (q) {
        const hay = `${r.assetCode} ${r.altName} ${r.name} ${r.modelName} ${r.subType} ${r.ownerCode} ${r.remarks}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [systemAssetRows, statusFilter, search]);

  // Quick lookup of master assets by assetCode for enriching AST rows.
  const assetByCode = useMemo(() => {
    const map = {};
    assets.forEach((a) => {
      if (a.assetCode) map[a.assetCode] = a;
    });
    return map;
  }, [assets]);

  // AST detail rows (flatten assetDetails across all AST vouchers).
  const astDetailRows = useMemo(() => {
    const rows = [];
    astRecords.forEach((rec) => {
      // Sub-location filter on the voucher itself.
      if (clientIds.length && idOf(rec.client) && !clientIds.includes(idOf(rec.client))) return;
      const details = Array.isArray(rec.assetDetails) ? rec.assetDetails : [];
      details.forEach((d, idx) => {
        const code = matchedAssetCodeOf(d);
        const masterAsset = code ? assetByCode[code] : null;
        // Apply asset-type/sub-type/model filters (joined via master).
        const anyAssetFilter =
          assetTypeIds.length || assetSubTypeIds.length || assetModelIds.length;
        if (anyAssetFilter && !assetPassesFilters(masterAsset)) return;
        const classification = classifyAst(d);
        const linkCode = linkCodeOf(d);
        const link = linkCode ? linksByCode[linkCode] : null;
        const { qr, tagType } = qrFromLink(link);
        rows.push({
          key: `${rec._id}-${idx}`,
          voucherNumber: rec.astNo || rec.voucherNumber || rec.voucherNo || rec._id,
          date: d.time || rec.createdAt,
          matchedCode: code,
          matchedName:
            (masterAsset && (masterAsset.altName || nameOf(masterAsset.assetModel))) || '',
          altName: (masterAsset && masterAsset.altName) || '',
          ownerCode:
            (masterAsset &&
              (masterAsset.ownerCode ||
                (masterAsset.owner &&
                  (masterAsset.owner.code || masterAsset.owner.employeeCode)))) ||
            '',
          entryType: entryTypeLabel(d),
          qrCode: qr,
          linkCode,
          mismatched: !!d.isQrMismatched,
          tagType,
          condition: lookups.conditionsById[d.condition] || '',
          remarks: d.remarks || '',
          enteredBy: lookups.usersById[idOf(d.user) || d.user] || '',
          latitude: d.latitude != null ? d.latitude : (rec.latitude != null ? rec.latitude : ''),
          longitude: d.longitude != null ? d.longitude : (rec.longitude != null ? rec.longitude : ''),
          status: classification,
          photos: collectPhotos(d),
        });
      });
    });
    return rows;
  }, [astRecords, assetByCode, linksByCode, lookups, qrFromLink, clientIds, assetTypeIds, assetSubTypeIds, assetModelIds, assetPassesFilters]);

  // AST rows filtered by the per-tab search box (matches asset code, name,
  // tag ID, QR value, remarks, or entered-by).
  const filteredAstRows = useMemo(() => {
    const q = (astSearch || '').toLowerCase().trim();
    if (!q) return astDetailRows;
    return astDetailRows.filter((r) => {
      const hay = `${r.matchedCode} ${r.matchedName} ${r.altName} ${r.ownerCode} ${r.linkCode} ${r.qrCode} ${r.remarks} ${r.enteredBy}`.toLowerCase();
      return hay.includes(q);
    });
  }, [astDetailRows, astSearch]);

  // PV detail rows. PV entries carry their own assetType/assetSubType/assetModel
  // ids (populated by the server), so the asset filters apply directly.
  const pvDetailRows = useMemo(() => {
    const rows = [];
    pvRecords.forEach((rec) => {
      if (clientIds.length && idOf(rec.client) && !clientIds.includes(idOf(rec.client))) return;
      const details = Array.isArray(rec.assetDetails) ? rec.assetDetails : [];
      details.forEach((d, idx) => {
        const dTypeId = idOf(d.assetType);
        const dSubTypeId = idOf(d.assetSubType) || idOf(d.equipmentSubType);
        const dModelId = idOf(d.assetModel);
        if (assetTypeIds.length && !assetTypeIds.includes(dTypeId)) return;
        if (assetSubTypeIds.length && !assetSubTypeIds.includes(dSubTypeId)) return;
        if (assetModelIds.length && !assetModelIds.includes(dModelId)) return;
        rows.push({
          key: `${rec._id}-${idx}`,
          voucherNumber: rec.voucherNumber || rec.code || rec._id,
          tempCode: tempCodeOf(d),
          date: rec.createdAt,
          name: d.proposedName || d.altName || d.name || d.serialNumber || '',
          assetType: nameOf(d.assetType),
          subType: nameOf(d.assetSubType) || nameOf(d.equipmentSubType),
          model: nameOf(d.assetModel),
          status: d.status || rec.status || '',
          photos: collectPhotos(d),
        });
      });
    });
    return rows;
  }, [pvRecords, clientIds, assetTypeIds, assetSubTypeIds, assetModelIds]);

  // Coverage matrix by sub-type.
  const coverageMatrix = useMemo(() => {
    const bySub = {};
    const ensure = (k) => {
      if (!bySub[k]) {
        bySub[k] = {
          subType: k || '—',
          inSystem: 0,
          qrVerified: 0,
          newQrLinked: 0,
          qrMismatch: 0,
          noQr: 0,
          newPv: 0,
          pending: 0,
        };
      }
      return bySub[k];
    };
    systemAssetRows.forEach((r) => {
      const row = ensure(r.subType);
      row.inSystem += 1;
      if (!r.verified) row.pending += 1;
      else if (r.statusCode === 'qr_verified') row.qrVerified += 1;
      else if (r.statusCode === 'new_qr_linked') row.newQrLinked += 1;
      else if (r.statusCode === 'qr_mismatch') row.qrMismatch += 1;
      else if (r.statusCode === 'verified_no_qr') row.noQr += 1;
    });
    pvDetailRows.forEach((r) => {
      const row = ensure(r.subType);
      row.newPv += 1;
    });
    return Object.values(bySub).sort((a, b) => b.inSystem - a.inSystem);
  }, [systemAssetRows, pvDetailRows]);

  // Summary tile counts.
  const summary = useMemo(() => {
    const inSystem = systemAssetRows.length;
    const verified = systemAssetRows.filter((r) => r.verified).length;
    const qrVerified = systemAssetRows.filter((r) => r.statusCode === 'qr_verified').length;
    const newQrLinked = systemAssetRows.filter((r) => r.statusCode === 'new_qr_linked').length;
    const qrMismatch = systemAssetRows.filter((r) => r.statusCode === 'qr_mismatch').length;
    const verifiedNoQr = systemAssetRows.filter((r) => r.statusCode === 'verified_no_qr').length;
    const qrInstalled = qrVerified + newQrLinked;
    const newPv = pvDetailRows.length;
    const pending = inSystem - verified;
    return {
      inSystem,
      verified,
      qrInstalled,
      qrVerified,
      newQrLinked,
      qrMismatch,
      verifiedNoQr,
      newPv,
      pending,
    };
  }, [systemAssetRows, pvDetailRows]);

  // ===== Export helpers =====

  const exportSystemAssetsCsv = () => {
    const rows = filteredSystemRows.map((r) => ({
      'Asset Code': r.assetCode,
      'Alt Name': r.altName,
      Model: r.modelName,
      'Sub-Type': r.subType,
      Owner: r.ownerCode,
      'Audit Status': r.verified ? 'Verified' : 'Pending',
      'QR Status': r.statusLabel,
      'Entry Type': r.entryType,
      'AST Voucher': r.astVoucherNumber,
      'Verifications': r.astCount,
      'QR Link (AT-N)': r.linkCode,
      'QR Value': r.qrCode,
      'Linked QRs': r.linkedQrsText,
      'Linked QR Count': r.linkedQrsCount,
      Remarks: r.remarks,
      Photos: r.photoUrls.length,
    }));
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-coverage-${moment().format('YYYYMMDD-HHmm')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportSystemAssetsPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    const locName = locationIds
      .map((id) => (locations.find((l) => l._id === id) || {}).name || '')
      .filter(Boolean)
      .join(', ');
    const voucherName =
      (vouchers.find((v) => v._id === voucherId) || {}).voucherNumber ||
      (vouchers.find((v) => v._id === voucherId) || {}).name ||
      '';
    doc.setFontSize(14);
    doc.text(`Audit Coverage Report — ${locName}`, 14, 14);
    doc.setFontSize(10);
    doc.text(
      `Voucher: ${voucherName}   Generated: ${moment().format('YYYY-MM-DD HH:mm')}`,
      14,
      20
    );
    doc.text(
      `In System: ${summary.inSystem}   Verified: ${summary.verified}   QR Installed: ${summary.qrInstalled}   Pending: ${summary.pending}   New (PV): ${summary.newPv}`,
      14,
      26
    );
    const body = filteredSystemRows.map((r) => [
      r.assetCode,
      r.altName,
      r.modelName,
      r.subType,
      r.ownerCode,
      r.verified ? 'Verified' : 'Pending',
      r.statusLabel,
      r.entryType,
      r.linkCode,
      r.linkedQrsText || r.qrCode,
    ]);
    doc.autoTable({
      startY: 32,
      head: [[
        'Asset Code',
        'Alt Name',
        'Model',
        'Sub-Type',
        'Owner',
        'Audit',
        'Status',
        'Entry',
        'Tag ID',
        'Linked QRs',
      ]],
      body,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 119, 255] },
    });
    doc.save(`audit-coverage-${moment().format('YYYYMMDD-HHmm')}.pdf`);
  };

  const openPhotoModal = (urls, title) => {
    if (!urls || !urls.length) {
      message.info('No photos available');
      return;
    }
    setPhotoModal({ visible: true, urls, title: title || 'Photos' });
  };

  // ===== Render =====

  const tileCard = (title, value, footer, color) => (
    <Col {...SUMMARY_TILE_COL} style={{ marginBottom: 12 }}>
      <Card bodyStyle={{ padding: 16 }}>
        <Statistic
          title={title}
          value={value}
          valueStyle={{ color: color || '#1677ff', fontSize: 26 }}
        />
        {footer && (
          <div style={{ marginTop: 6, color: '#888', fontSize: 12 }}>{footer}</div>
        )}
      </Card>
    </Col>
  );

  const photoCell = (urls, title) => {
    if (!urls || !urls.length) return <Tag>—</Tag>;
    return (
      <a onClick={() => openPhotoModal(urls, title)}>
        <Icon type="picture" /> {urls.length}
      </a>
    );
  };

  const systemColumns = [
    {
      title: 'Asset Code',
      dataIndex: 'assetCode',
      key: 'assetCode',
      width: 130,
      sorter: (a, b) => (a.assetCode || '').localeCompare(b.assetCode || ''),
      fixed: 'left',
    },
    {
      title: 'Alt Name',
      dataIndex: 'altName',
      key: 'altName',
      width: 160,
      ellipsis: true,
      render: (v) =>
        v ? <Tooltip title={v}>{v}</Tooltip> : <span style={{ color: '#bbb' }}>—</span>,
    },
    {
      title: 'Model',
      dataIndex: 'modelName',
      key: 'modelName',
      width: 160,
      ellipsis: true,
      render: (v) => <Tooltip title={v}>{v}</Tooltip>,
    },
    {
      title: 'Sub-Type',
      dataIndex: 'subType',
      key: 'subType',
      width: 140,
      ellipsis: true,
      render: (v) => <Tooltip title={v}>{v}</Tooltip>,
    },
    { title: 'Owner', dataIndex: 'ownerCode', key: 'ownerCode', width: 100 },
    {
      title: 'Audit Status',
      key: 'verified',
      width: 120,
      filters: [
        { text: 'Verified', value: true },
        { text: 'Pending', value: false },
      ],
      onFilter: (val, rec) => rec.verified === val,
      render: (_, r) =>
        r.verified ? (
          <Tag color="green">Verified</Tag>
        ) : (
          <Tag color="orange">Pending</Tag>
        ),
    },
    {
      title: 'QR Status',
      key: 'hasQr',
      width: 170,
      render: (_, r) => {
        if (!r.verified) return <Tag color="default">Not Audited</Tag>;
        const tag = <Tag color={r.statusColor}>{r.statusLabel}</Tag>;
        if (r.astCount > 1) {
          return (
            <span>
              {tag}
              <Tooltip title={`${r.astCount} verifications — click to view all`}>
                <a
                  onClick={() =>
                    setVerificationsModal({
                      visible: true,
                      assetCode: r.assetCode,
                      entries: r.astEntries,
                    })
                  }
                  style={{ marginLeft: 4 }}
                >
                  <Tag color="purple" style={{ cursor: 'pointer' }}>
                    ×{r.astCount}
                  </Tag>
                </a>
              </Tooltip>
            </span>
          );
        }
        return tag;
      },
    },
    {
      title: 'Entry Type',
      dataIndex: 'entryType',
      key: 'entryType',
      width: 110,
      render: (v) => v || '—',
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 200,
      ellipsis: true,
      render: (v) =>
        v ? (
          <Tooltip title={v}>
            <span>{v}</span>
          </Tooltip>
        ) : (
          <span style={{ color: '#bbb' }}>—</span>
        ),
    },
    {
      title: 'AST Voucher',
      dataIndex: 'astVoucherNumber',
      key: 'astVoucherNumber',
      width: 130,
    },
    { title: 'QR Link', dataIndex: 'linkCode', key: 'linkCode', width: 110 },
    {
      title: 'Linked QRs',
      key: 'linkedQrs',
      width: 220,
      filters: [
        { text: 'Has QR(s)', value: 'has' },
        { text: 'None', value: 'none' },
      ],
      onFilter: (val, rec) =>
        val === 'has' ? rec.linkedQrsCount > 0 : rec.linkedQrsCount === 0,
      render: (_, r) => {
        if (!r.linkedQrsCount) return <Tag color="default">None</Tag>;
        const text = r.linkedQrsText;
        return (
          <Tooltip title={text}>
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
              {r.linkedQrsCount > 1 ? (
                <Tag color="blue" style={{ marginRight: 4 }}>{r.linkedQrsCount}</Tag>
              ) : null}
              {text}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Photos',
      key: 'photos',
      width: 90,
      render: (_, r) => photoCell(r.photoUrls, `${r.assetCode} photos`),
    },
  ];

  const astColumns = [
    { title: 'Asset Code', dataIndex: 'matchedCode', key: 'matchedCode', width: 120, fixed: 'left' },
    {
      title: 'Asset Name',
      dataIndex: 'matchedName',
      key: 'matchedName',
      width: 160,
      ellipsis: true,
      render: (v) => <Tooltip title={v}>{v}</Tooltip>,
    },
    {
      title: 'Alt Name',
      dataIndex: 'altName',
      key: 'altName',
      width: 140,
      ellipsis: true,
      render: (v) =>
        v ? <Tooltip title={v}>{v}</Tooltip> : <span style={{ color: '#bbb' }}>—</span>,
    },
    { title: 'Owner', dataIndex: 'ownerCode', key: 'ownerCode', width: 120 },
    {
      title: 'Entry Type',
      dataIndex: 'entryType',
      key: 'entryType',
      width: 110,
      filters: [
        { text: 'QR Code', value: 'qr' },
        { text: 'Manual', value: 'manual' },
      ],
      onFilter: (val, rec) =>
        val === 'qr' ? /qr/i.test(rec.entryType) : /manual/i.test(rec.entryType),
      render: (v) => v || '—',
    },
    {
      title: 'Status',
      key: 'status',
      width: 150,
      filters: [
        { text: 'QR Verified', value: 'qr_verified' },
        { text: 'QR Mismatch', value: 'qr_mismatch' },
        { text: 'New QR Linked', value: 'new_qr_linked' },
        { text: 'Verified, No QR', value: 'verified_no_qr' },
      ],
      onFilter: (val, rec) => rec.status && rec.status.code === val,
      render: (_, r) =>
        r.status ? <Tag color={r.status.color}>{r.status.label}</Tag> : '—',
    },
    {
      title: 'QR Mismatch?',
      dataIndex: 'mismatched',
      key: 'mismatched',
      width: 110,
      render: (v) => (v ? <Tag color="red">Yes</Tag> : <Tag>No</Tag>),
    },
    { title: 'Tag ID', dataIndex: 'linkCode', key: 'linkCode', width: 100 },
    { title: 'QR', dataIndex: 'qrCode', key: 'qrCode', width: 140 },
    { title: 'Tag Type', dataIndex: 'tagType', key: 'tagType', width: 180 },
    { title: 'Condition', dataIndex: 'condition', key: 'condition', width: 100 },
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', width: 180, ellipsis: true },
    { title: 'Entered By', dataIndex: 'enteredBy', key: 'enteredBy', width: 130 },
    {
      title: 'Time',
      dataIndex: 'date',
      key: 'date',
      width: 140,
      render: (d) => (d ? moment(d).format('YYYY-MM-DD HH:mm') : ''),
    },
    { title: 'AST #', dataIndex: 'voucherNumber', key: 'voucherNumber', width: 130 },
    {
      title: 'Photos',
      key: 'photos',
      width: 80,
      render: (_, r) => photoCell(r.photos, `AST ${r.matchedCode}`),
    },
  ];

  const pvColumns = [
    { title: 'PV #', dataIndex: 'voucherNumber', key: 'voucherNumber', width: 140 },
    { title: 'Temp Code', dataIndex: 'tempCode', key: 'tempCode', width: 130 },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 140,
      render: (d) => (d ? moment(d).format('YYYY-MM-DD HH:mm') : ''),
    },
    { title: 'Proposed Name', dataIndex: 'name', key: 'name' },
    { title: 'Asset Type', dataIndex: 'assetType', key: 'assetType' },
    { title: 'Sub-Type', dataIndex: 'subType', key: 'subType' },
    { title: 'Model', dataIndex: 'model', key: 'model' },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 110 },
    {
      title: 'Photos',
      key: 'photos',
      width: 90,
      render: (_, r) => photoCell(r.photos, `PV ${r.tempCode || r.voucherNumber}`),
    },
  ];

  const matrixColumns = [
    { title: 'Sub-Type', dataIndex: 'subType', key: 'subType' },
    { title: 'In System', dataIndex: 'inSystem', key: 'inSystem', align: 'right' },
    { title: 'QR Verified', dataIndex: 'qrVerified', key: 'qrVerified', align: 'right' },
    { title: 'New QR Linked', dataIndex: 'newQrLinked', key: 'newQrLinked', align: 'right' },
    { title: 'QR Mismatch', dataIndex: 'qrMismatch', key: 'qrMismatch', align: 'right' },
    { title: 'No QR', dataIndex: 'noQr', key: 'noQr', align: 'right' },
    { title: 'New (PV)', dataIndex: 'newPv', key: 'newPv', align: 'right' },
    { title: 'Pending', dataIndex: 'pending', key: 'pending', align: 'right' },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={
          <span>
            <Icon type="safety-certificate" /> Audit Coverage Report
          </span>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[12, 12]} type="flex" align="bottom">
          <Col xs={24} sm={12} md={6}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Location</div>
            <Select
              mode="multiple"
              maxTagCount="responsive"
              placeholder="All"
              style={{ width: '100%' }}
              value={locationIds}
              onChange={(v) => setLocationIds(v || [])}
              allowClear
              showSearch
              optionFilterProp="children"
              loading={bootLoading}
            >
              {locations.map((l) => (
                <Option key={l._id} value={l._id}>
                  {l.name || l.code}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Sub Location</div>
            <Select
              mode="multiple"
              maxTagCount="responsive"
              placeholder="All"
              style={{ width: '100%' }}
              value={clientIds}
              onChange={(v) => setClientIds(v || [])}
              allowClear
              showSearch
              optionFilterProp="children"
              loading={bootLoading}
            >
              {clients.map((c) => (
                <Option key={c._id} value={c._id}>
                  {c.name || c.code}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Asset Type</div>
            <Select
              mode="multiple"
              maxTagCount="responsive"
              placeholder="All"
              style={{ width: '100%' }}
              value={assetTypeIds}
              onChange={(v) => setAssetTypeIds(v || [])}
              allowClear
              showSearch
              optionFilterProp="children"
              loading={bootLoading}
            >
              {assetTypes.map((t) => (
                <Option key={t._id} value={t._id}>
                  {t.name || t.code}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
              Asset Sub-Type
              {assetTypeIds.length ? (
                <span style={{ color: '#bbb' }}> ({subTypeOptions.length})</span>
              ) : null}
            </div>
            <Select
              mode="multiple"
              maxTagCount="responsive"
              placeholder="All"
              style={{ width: '100%' }}
              value={assetSubTypeIds}
              onChange={(v) => setAssetSubTypeIds(v || [])}
              allowClear
              showSearch
              optionFilterProp="children"
              loading={bootLoading}
            >
              {subTypeOptions.map((s) => (
                <Option key={s._id} value={s._id}>
                  {s.name || s.code}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
              Asset Model
              {(assetTypeIds.length || assetSubTypeIds.length) ? (
                <span style={{ color: '#bbb' }}> ({modelOptions.length})</span>
              ) : null}
            </div>
            <Select
              mode="multiple"
              maxTagCount="responsive"
              placeholder="All"
              style={{ width: '100%' }}
              value={assetModelIds}
              onChange={(v) => setAssetModelIds(v || [])}
              allowClear
              showSearch
              optionFilterProp="children"
              loading={bootLoading}
            >
              {modelOptions.map((m) => (
                <Option key={m._id} value={m._id}>
                  {m.name || m.code}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={10}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
              Audit Voucher ({filteredVouchers.length})
            </div>
            <Select
              placeholder="Select voucher"
              style={{ width: '100%' }}
              value={voucherId || undefined}
              onChange={(v) => setVoucherId(v || null)}
              allowClear
              showSearch
              optionFilterProp="children"
              loading={bootLoading}
            >
              {filteredVouchers.map((v) => (
                <Option key={v._id} value={v._id}>
                  {(v.voucherNo || v.voucherNumber || v.name || v._id) +
                    (v.name && (v.voucherNo || v.voucherNumber)
                      ? `  •  ${v.name}`
                      : '') +
                    (v.createdAt
                      ? `  •  ${moment(v.createdAt).format('YYYY-MM-DD')}`
                      : '')}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button
              type="primary"
              icon="reload"
              onClick={fetchAuditData}
              loading={loading}
              disabled={!locationIds.length}
              block
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {!locationIds.length && !bootLoading && (
        <Alert
          type="info"
          message="Pick a client, location, and audit voucher to begin."
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={loading}>
        <Row gutter={12}>
          {tileCard('Assets in System', summary.inSystem, 'Master at this location', '#1677ff')}
          {tileCard(
            'Verified via AST',
            summary.verified,
            summary.inSystem
              ? `${Math.round((summary.verified / summary.inSystem) * 100)}% of system`
              : '',
            '#52c41a'
          )}
          {tileCard(
            'QR Verified',
            summary.qrVerified,
            'Scanned QR matched system',
            '#52c41a'
          )}
          {tileCard(
            'New QR Linked',
            summary.newQrLinked,
            'First-time QR tagged during this audit',
            '#1677ff'
          )}
          {tileCard(
            'QR Mismatch',
            summary.qrMismatch,
            'Physical QR ≠ system — needs reconciliation',
            '#f5222d'
          )}
          {tileCard(
            'Verified, No QR',
            summary.verifiedNoQr,
            'Visually verified but no QR found / linked',
            '#fa8c16'
          )}
          {tileCard('New Assets (PV)', summary.newPv, 'Discovered at site, not in master', '#722ed1')}
          {tileCard(
            'Pending',
            summary.pending,
            summary.inSystem
              ? `${Math.round((summary.pending / summary.inSystem) * 100)}% of system`
              : '',
            '#f5222d'
          )}
        </Row>

        <Card style={{ marginTop: 12 }} bodyStyle={{ paddingTop: 8 }}>
          <Tabs defaultActiveKey="system">
            <TabPane tab={`System Assets (${filteredSystemRows.length})`} key="system">
              <Row gutter={8} style={{ marginBottom: 12 }} type="flex" align="middle">
                <Col>
                  <Radio.Group
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <Radio.Button value="all">All</Radio.Button>
                    <Radio.Button value="verified">Verified</Radio.Button>
                    <Radio.Button value="pending">Pending</Radio.Button>
                    <Radio.Button value="qr_verified">QR Verified</Radio.Button>
                    <Radio.Button value="new_qr_linked">New QR Linked</Radio.Button>
                    <Radio.Button value="qr_mismatch">QR Mismatch</Radio.Button>
                    <Radio.Button value="verified_no_qr">No QR</Radio.Button>
                  </Radio.Group>
                </Col>
                <Col flex="auto">
                  <Input.Search
                    placeholder="Search by code, name, model, owner…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    allowClear
                    style={{ maxWidth: 360, marginLeft: 8 }}
                  />
                </Col>
                <Col>
                  <Tooltip title="Export CSV">
                    <Button icon="download" onClick={exportSystemAssetsCsv} style={{ marginRight: 6 }}>
                      CSV
                    </Button>
                  </Tooltip>
                  <Tooltip title="Export PDF">
                    <Button icon="file-pdf" onClick={exportSystemAssetsPdf}>
                      PDF
                    </Button>
                  </Tooltip>
                </Col>
              </Row>
              <Table
                size="small"
                columns={systemColumns}
                dataSource={filteredSystemRows}
                pagination={{ pageSize: 25, showSizeChanger: true }}
                scroll={{ x: 1900 }}
                locale={{ emptyText: <Empty description="No assets at this location" /> }}
              />
            </TabPane>

            <TabPane tab={`AST Records (${filteredAstRows.length}${astSearch ? ` / ${astDetailRows.length}` : ''})`} key="ast">
              <Row style={{ marginBottom: 12 }}>
                <Col>
                  <Input.Search
                    placeholder="Search asset code, name, tag, QR, remarks…"
                    value={astSearch}
                    onChange={(e) => setAstSearch(e.target.value)}
                    allowClear
                    style={{ width: 360 }}
                  />
                </Col>
              </Row>
              <Table
                size="small"
                columns={astColumns}
                dataSource={filteredAstRows}
                pagination={{ pageSize: 25, showSizeChanger: true }}
                scroll={{ x: 2100 }}
                locale={{
                  emptyText: <Empty description="No AST records for this voucher" />,
                }}
              />
            </TabPane>

            <TabPane tab={`PV Records (${pvDetailRows.length})`} key="pv">
              <Table
                size="small"
                columns={pvColumns}
                dataSource={pvDetailRows}
                pagination={{ pageSize: 25, showSizeChanger: true }}
                scroll={{ x: 1200 }}
                locale={{
                  emptyText: <Empty description="No PV records for this voucher" />,
                }}
              />
            </TabPane>

            <TabPane tab={`Coverage Matrix (${coverageMatrix.length})`} key="matrix">
              <Table
                size="small"
                columns={matrixColumns}
                dataSource={coverageMatrix.map((r, i) => ({ key: i, ...r }))}
                pagination={false}
                summary={(pageData) => {
                  const sum = pageData.reduce(
                    (acc, r) => ({
                      inSystem: acc.inSystem + r.inSystem,
                      qrVerified: acc.qrVerified + r.qrVerified,
                      newQrLinked: acc.newQrLinked + r.newQrLinked,
                      qrMismatch: acc.qrMismatch + r.qrMismatch,
                      noQr: acc.noQr + r.noQr,
                      newPv: acc.newPv + r.newPv,
                      pending: acc.pending + r.pending,
                    }),
                    {
                      inSystem: 0,
                      qrVerified: 0,
                      newQrLinked: 0,
                      qrMismatch: 0,
                      noQr: 0,
                      newPv: 0,
                      pending: 0,
                    }
                  );
                  return (
                    <tr style={{ fontWeight: 600, background: '#fafafa' }}>
                      <td style={{ padding: 8 }}>Total</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{sum.inSystem}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{sum.qrVerified}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{sum.newQrLinked}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{sum.qrMismatch}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{sum.noQr}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{sum.newPv}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{sum.pending}</td>
                    </tr>
                  );
                }}
              />
            </TabPane>
          </Tabs>
        </Card>
      </Spin>

      <Modal
        title={photoModal.title}
        visible={photoModal.visible}
        footer={null}
        width={720}
        onCancel={() => setPhotoModal({ visible: false, urls: [], title: '' })}
      >
        <Row gutter={8}>
          {photoModal.urls.map((u, i) => (
            <Col key={i} span={12} style={{ marginBottom: 8 }}>
              <a href={u} target="_blank" rel="noopener noreferrer">
                <img
                  src={u}
                  alt={`photo-${i}`}
                  style={{
                    width: '100%',
                    maxHeight: 280,
                    objectFit: 'cover',
                    border: '1px solid #eee',
                    borderRadius: 4,
                  }}
                />
              </a>
            </Col>
          ))}
        </Row>
      </Modal>

      <Modal
        title={`Verifications — ${verificationsModal.assetCode} (${verificationsModal.entries.length})`}
        visible={verificationsModal.visible}
        footer={null}
        width={1180}
        onCancel={() =>
          setVerificationsModal({ visible: false, assetCode: '', entries: [] })
        }
      >
        <Table
          size="small"
          rowKey={(e) => e.detail._id || `${e.voucher._id}-${e.time}`}
          dataSource={verificationsModal.entries}
          pagination={false}
          scroll={{ x: 1100 }}
          columns={[
            {
              title: 'Time',
              dataIndex: 'time',
              key: 'time',
              width: 140,
              render: (t) => (t ? moment(t).format('YYYY-MM-DD HH:mm') : ''),
            },
            {
              title: 'Entry Type',
              dataIndex: 'entryType',
              key: 'entryType',
              width: 100,
            },
            {
              title: 'Status',
              key: 'status',
              width: 140,
              render: (_, e) =>
                e.classification ? (
                  <Tag color={e.classification.color}>{e.classification.label}</Tag>
                ) : null,
            },
            { title: 'Tag ID', dataIndex: 'linkCode', key: 'linkCode', width: 90 },
            { title: 'QR', dataIndex: 'qrCode', key: 'qrCode', width: 130 },
            {
              title: 'Remarks',
              dataIndex: 'remarks',
              key: 'remarks',
              width: 260,
              ellipsis: true,
              render: (v) =>
                v ? (
                  <Tooltip title={v}>
                    <span>{v}</span>
                  </Tooltip>
                ) : (
                  <span style={{ color: '#bbb' }}>—</span>
                ),
            },
            {
              title: 'Entered By',
              dataIndex: 'userName',
              key: 'userName',
              width: 130,
            },
            {
              title: 'Photos',
              key: 'photos',
              width: 80,
              render: (_, e) =>
                e.photos && e.photos.length ? (
                  <a
                    onClick={() =>
                      openPhotoModal(e.photos, `${verificationsModal.assetCode} — ${moment(e.time).format('YYYY-MM-DD HH:mm')}`)
                    }
                  >
                    <Icon type="picture" /> {e.photos.length}
                  </a>
                ) : (
                  <span style={{ color: '#bbb' }}>—</span>
                ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default AuditCoverageReport;
